import * as React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { ShellContext } from './layout/AppShell';
import { SectionCard } from './ui';
import { TRUE_COLORS } from '../theme';
import { MenuView, Menu } from './business/BusinessPage';
import { BrandFont, PublicBrand, completeColors, useFontsStylesheet } from './business/brand';

type BrandDraft = {
  background: string; surface: string; text: string; primary: string; accent: string;
  heading_font: string; body_font: string; tagline: string;
  /** What the business calls its products on its pages: Menú, Catálogo, Productos… */
  catalog_label: string;
  /** The business's privacy notice (https link; the API sends '' when there is none). */
  privacy_url: string;
  /** Its own website where customers order (https link; '' when there is none). */
  order_url: string;
};
type BrandPayload = {
  brand: BrandDraft;
  defaults: BrandDraft;
  fonts: BrandFont[];
  fonts_css_all: string;
  logo: string | null;
  cover: string | null;
};

const COLOR_FIELDS: { key: keyof BrandDraft; label: string; hint: string }[] = [
  { key: 'background', label: 'Fondo', hint: 'El fondo de la página' },
  { key: 'surface', label: 'Tarjetas', hint: 'Encabezado y productos' },
  { key: 'text', label: 'Texto', hint: 'Títulos, precios y textos' },
  { key: 'primary', label: 'Principal', hint: 'Categorías y botones' },
  { key: 'accent', label: 'Acento', hint: 'Detalles junto a los títulos' },
];

const HEX = /^#[0-9A-Fa-f]{6}$/;

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return fallback;
};

const ImageField = ({ label, hint, url, busy, disabled, wide, onUpload, onRemove }: {
  label: string; hint: string; url: string | null; busy: boolean; disabled: boolean; wide?: boolean;
  onUpload: (file: File) => void; onRemove: () => void;
}) => {
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      <Box sx={{
        width: wide ? 160 : 96, height: 96, borderRadius: 2, flexShrink: 0, overflow: 'hidden', display: 'grid', placeItems: 'center',
        bgcolor: TRUE_COLORS.lightBg, border: `1px solid ${TRUE_COLORS.borderSolid}`,
      }}>
        {busy ? <CircularProgress size={22} />
          : url ? <Box component="img" src={url} alt={label} sx={{ width: '100%', height: '100%', objectFit: wide ? 'cover' : 'contain', p: wide ? 0 : 1 }} />
          : <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin {label.toLowerCase()}</Typography>}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 1 }}>{hint}</Typography>
        {!disabled && (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<UploadOutlinedIcon />} onClick={() => input.current?.click()} disabled={busy}>
              {url ? 'Cambiar' : 'Subir'}
            </Button>
            {url && <Button size="small" color="inherit" onClick={onRemove} disabled={busy}>Quitar</Button>}
          </Stack>
        )}
        <input ref={input} hidden type="file" accept="image/png,image/jpeg,image/webp"
          onChange={e => { const file = e.target.files?.[0]; if (file) onUpload(file); e.target.value = ''; }} />
      </Box>
    </Stack>
  );
};

/** /admin/brand: logo, cover, colors, fonts and tagline of the business's public pages, with a live preview. */
const BrandSettings = () => {
  const axios = useAxios();
  const { membership } = React.useContext(ShellContext);
  const isAdmin = !!membership?.is_admin;

  const [payload, setPayload] = React.useState<BrandPayload | null>(null);
  const [draft, setDraft] = React.useState<BrandDraft | null>(null);
  const [organization, setOrganization] = React.useState<{ name: string; slug: string } | null>(null);
  const [menu, setMenu] = React.useState<Menu | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState<null | 'logo' | 'cover'>(null);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  const load = React.useCallback((data: BrandPayload) => { setPayload(data); setDraft(data.brand); }, []);

  React.useEffect(() => {
    axios.get('management/organization/brand/').then(r => load(r.data)).catch(err => setError(errorText(err, 'No se pudo cargar la marca.')));
    axios.get('management/organization/').then(r => setOrganization(r.data)).catch(() => {});
  }, [axios, load]);

  React.useEffect(() => {
    if (!organization?.slug) return;
    fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${organization.slug}/catalog/`)
      .then(r => (r.ok ? r.json() : null)).then(setMenu).catch(() => {});
  }, [organization?.slug]);

  useFontsStylesheet(payload?.fonts_css_all);

  const set = (key: keyof BrandDraft, value: string) => setDraft(d => (d ? { ...d, [key]: value } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true); setError(''); setNotice('');
    try {
      const { data } = await axios.put('management/organization/brand/', draft);
      load(data);
      setNotice('Marca guardada. Tu catálogo en línea ya la usa.');
    } catch (err) {
      setError(errorText(err, 'No se pudo guardar la marca.'));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (kind: 'logo' | 'cover', file: File) => {
    setUploading(kind); setError(''); setNotice('');
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`management/organization/brand/${kind}/`, form);
      setPayload(data);
    } catch (err) {
      setError(errorText(err, 'No se pudo subir la imagen.'));
    } finally {
      setUploading(null);
    }
  };

  const remove = async (kind: 'logo' | 'cover') => {
    setUploading(kind); setError('');
    try {
      const { data } = await axios.delete(`management/organization/brand/${kind}/`);
      setPayload(data);
    } catch (err) {
      setError(errorText(err, 'No se pudo quitar la imagen.'));
    } finally {
      setUploading(null);
    }
  };

  const fontById = (id: string) => payload?.fonts.find(f => f.id === id) ?? payload?.fonts[0] ?? { id: 'inter', label: 'Inter', family: 'Inter', stretch: null };
  const colorsValid = draft ? COLOR_FIELDS.every(f => HEX.test(draft[f.key])) : false;
  const preview: PublicBrand | null = draft && payload && colorsValid ? {
    colors: completeColors({ background: draft.background, surface: draft.surface, text: draft.text, primary: draft.primary, accent: draft.accent }),
    fonts: { heading: fontById(draft.heading_font), body: fontById(draft.body_font) },
    fonts_css: payload.fonts_css_all,
    tagline: draft.tagline,
    catalog_label: draft.catalog_label || 'Menú',
    privacy_url: draft.privacy_url ?? '',
    order_url: draft.order_url ?? '',
    logo: payload.logo,
    cover: payload.cover,
  } : null;
  const changed = !!(payload && draft && JSON.stringify(payload.brand) !== JSON.stringify(draft));

  return (
    <PageShell
      title="Marca y catálogo"
      subtitle={organization?.slug ? `Así se ven tu catálogo en línea y tus páginas en receipt.com.mx/${organization.slug}` : 'Así se ven tu catálogo en línea y tus páginas públicas'}
      actions={
        <>
          {organization?.slug && (
            <Button color="inherit" endIcon={<OpenInNewIcon />} href={`/${organization.slug}`} target="_blank" rel="noopener">Ver mi catálogo</Button>
          )}
          {isAdmin && <Button variant="contained" onClick={save} disabled={!changed || saving}>{saving ? 'Guardando…' : 'Guardar marca'}</Button>}
        </>
      }
    >
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 3 }}>{notice}</Alert>}
      {!isAdmin && <Alert severity="info" sx={{ mb: 3 }}>Solo un administrador puede cambiar la marca.</Alert>}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          {!payload || !draft ? <Skeleton variant="rounded" height={520} /> : (
            <Stack spacing={3}>
              <SectionCard title="Logo y portada" subtitle="Usa el logo tal cual lo indica tu manual de marca, sin sombras ni efectos.">
                <Stack spacing={2.5}>
                  <ImageField label="Logo" hint="PNG con fondo transparente, hasta 5 MB." url={payload.logo} busy={uploading === 'logo'} disabled={!isAdmin}
                    onUpload={file => upload('logo', file)} onRemove={() => remove('logo')} />
                  <ImageField label="Portada" hint="Foto horizontal de tu negocio o tus productos (JPG o WebP), hasta 10 MB." url={payload.cover} wide busy={uploading === 'cover'} disabled={!isAdmin}
                    onUpload={file => upload('cover', file)} onRemove={() => remove('cover')} />
                  <TextField label="Frase" value={draft.tagline} disabled={!isAdmin} onChange={e => set('tagline', e.target.value)}
                    helperText="Aparece junto a tu logo." slotProps={{ htmlInput: { maxLength: 120 } }} />
                  <TextField label="Nombre de tu catálogo" value={draft.catalog_label ?? ''} disabled={!isAdmin} onChange={e => set('catalog_label', e.target.value)}
                    helperText="Cómo lo verán tus clientes, p. ej. Menú, Catálogo, Productos o Servicios." slotProps={{ htmlInput: { maxLength: 30 } }} />
                  <TextField label="Aviso de privacidad (enlace)" placeholder="https://" value={draft.privacy_url ?? ''} disabled={!isAdmin} onChange={e => set('privacy_url', e.target.value)}
                    helperText="Tu catálogo y la tarjeta de lealtad de tus clientes lo enlazan. Necesario si guardas sus datos." slotProps={{ htmlInput: { maxLength: 300, inputMode: 'url' } }} />
                  <TextField label="Tu sitio web para pedidos (opcional)" placeholder="https://" value={draft.order_url ?? ''} disabled={!isAdmin} onChange={e => set('order_url', e.target.value)}
                    helperText="Si tus clientes piden desde tu propio sitio, el botón «Pedir» de su tarjeta los lleva ahí con su tarjeta, para usar sus recompensas. Vacío: tu catálogo de TRUE."
                    slotProps={{ htmlInput: { maxLength: 300, inputMode: 'url', 'data-testid': 'brand-order-url' } }} />
                </Stack>
              </SectionCard>

              <SectionCard title="Colores" subtitle="Los de tu manual de marca. El texto sobre cada color se ajusta solo para que se lea bien.">
                <Grid container spacing={2}>
                  {COLOR_FIELDS.map(field => (
                    <Grid key={field.key} size={{ xs: 12, sm: 6 }}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                        <Box component="input" type="color" aria-label={field.label} disabled={!isAdmin}
                          value={HEX.test(draft[field.key]) ? draft[field.key] : '#000000'}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set(field.key, e.target.value.toUpperCase())}
                          sx={{ width: 44, height: 40, p: 0, border: `1px solid ${TRUE_COLORS.borderSolid}`, borderRadius: 1, bgcolor: 'transparent', cursor: isAdmin ? 'pointer' : 'default', flexShrink: 0 }} />
                        <TextField size="small" fullWidth label={field.label} value={draft[field.key]} disabled={!isAdmin}
                          error={!HEX.test(draft[field.key])} helperText={HEX.test(draft[field.key]) ? field.hint : 'Formato #RRGGBB'}
                          onChange={e => set(field.key, e.target.value.trim())} />
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>

              <SectionCard title="Tipografías">
                <Grid container spacing={2}>
                  {(['heading_font', 'body_font'] as const).map(key => (
                    <Grid key={key} size={{ xs: 12, sm: 6 }}>
                      <TextField select fullWidth label={key === 'heading_font' ? 'Títulos' : 'Textos'} value={draft[key]} disabled={!isAdmin} onChange={e => set(key, e.target.value)}>
                        {payload.fonts.map(font => (
                          <MenuItem key={font.id} value={font.id} sx={{ fontFamily: `'${font.family}', sans-serif`, fontStretch: font.stretch ?? 'normal' }}>{font.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ))}
                </Grid>
                {isAdmin && (
                  <Button size="small" color="inherit" sx={{ mt: 2 }} onClick={() => setDraft({ ...payload.defaults, tagline: draft.tagline })}>
                    Volver a los colores y tipografías de TRUE
                  </Button>
                )}
              </SectionCard>
            </Stack>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 96 } }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Vista previa {changed ? '(sin guardar)' : ''}</Typography>
            <Box sx={{
              width: '100%', maxWidth: 390, height: 680, mx: 'auto', overflowY: 'auto', overflowX: 'hidden', borderRadius: '28px',
              border: `8px solid ${TRUE_COLORS.primary}`, boxShadow: '0 12px 40px rgba(20,37,44,0.18)', bgcolor: '#fff',
            }}>
              {preview && menu ? <MenuView menu={menu} brand={preview} embedded />
                : <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><CircularProgress size={24} /></Box>}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default BrandSettings;
